import { useState } from 'react';

import { BackLink } from '@/components/BackLink';
import { Button } from '@/components/Button';
import { Callout } from '@/components/Callout';
import { Field } from '@/components/Field';
import { KioskCard } from '@/components/KioskCard';
import { KioskScreen } from '@/components/KioskScreen';
import { digitsOnly } from '@/features/application/types';
import type { TranslationKey } from '@/i18n/dictionary';
import { useTranslation } from '@/i18n/useTranslation';
import type { CheckInFactorName, CheckInFactors } from '@/lib/kioskSession';
import { LOOKUP_STATUS, useKioskSession } from '@/lib/kioskSession';
import { useFocusOnMount } from '@/shared/hooks/useFocusOnMount';

import type { CheckInField } from '../validation';
import { CHECK_IN_FIELD, fieldOf, invalidFields, SSN_LAST4_LENGTH } from '../validation';
import { DateOfBirthField } from './DateOfBirthField';

/**
 * The check-in screen [Figma · 02 · Check-in, all three viewports].
 *
 * KID-1 built the shell and KID-2 the lookup behind it; both were built without reading the frame, and this
 * is the corrected version. What changed is not cosmetic — the card, the logo, the sub-heading, the
 * dropdown date of birth and the callout are all content that was simply absent.
 *
 * **Continue is never disabled for an incomplete form, and it never spends an attempt on one.** Decision
 * 0032 makes the match an AND of all four factors, so the lookup fires only when all four are there — but
 * that guard belongs in the submit handler, not in a greyed-out button. A disabled primary withholds the
 * reason and leaves the candidate hunting for it; this screen now does what the prototype's own rule and
 * `ApplicationScreen` already do — block the step, say what is missing, and put the cursor in the first
 * gap. Corrected 2026-09-14: the date-of-birth message used to appear the instant one of the two dropdowns
 * was chosen, and the prototype defers it to the Continue press.
 *
 * **Every field says what is wrong with it, and a refused lookup is answered HERE** [ISS-147]. A blank email,
 * last name or SSN used to block Continue in silence, a malformed email or a three-digit SSN reached the api,
 * and the api's `400` was read as an outage — which lands on the neutral hand-off and its promise that a
 * representative has been told, with no entry behind it [0220 L1]. The shape of each factor is now checked
 * before a lookup is spent [`../validation.ts`], a shape the api still refuses comes back as
 * `rejectedFactors`, and either way the candidate stays on this form with what they typed. The messages are
 * the frame's own, from the `Validation message` slots `New user / 03 · Check-in` draws under each field.
 *
 * Back and the idle timeout end the same way — the candidate is gone, and everything they typed goes with
 * them (BR-K12, decision 0005) — but Back ASKS first [ISS-149]: it opens the Exit question, the same overlay
 * the top bar's `Exit` opens, and only its YES ends the session. NO leaves every typed factor in place. The
 * idle timeout still clears silently [decision 0248].
 */
export function CheckInScreen() {
  const t = useTranslation();
  const heading = useFocusOnMount<HTMLHeadingElement>();
  const headingKey = useKioskSession((state) => state.checkInCopy.headingKey);
  const factors = useKioskSession((state) => state.factors);
  const setFactor = useKioskSession((state) => state.setFactor);
  const askToExit = useKioskSession((state) => state.askToExit);
  const submitLookup = useKioskSession((state) => state.submitLookup);
  const lookupStatus = useKioskSession((state) => state.lookupStatus);
  const rejectedFactors = useKioskSession((state) => state.rejectedFactors);

  const isPending = lookupStatus === LOOKUP_STATUS.pending;
  /*
   * Counts REFUSED Continues, and nothing else drives the messages on this form.
   *
   * A counter rather than a boolean for the same reason `useScrollToFirstError` takes one: a second press
   * with the same gap still has to answer. Zero means the candidate has not asked yet, so the form stays
   * silent — including the reserved date-of-birth line.
   */
  const [refusals, setRefusals] = useState(0);
  const invalid = invalidFields(factors);
  const refused = new Set(rejectedFactors.map(fieldOf));
  /*
   * A field speaks when the candidate has pressed Continue and it is still wrong, or when the api refused it
   * on the last lookup — the second survives the checking screen, which the first cannot.
   */
  const errorFor = (field: CheckInField, message: TranslationKey): string | undefined =>
    (refusals > 0 && invalid.includes(field)) || refused.has(field) ? t(message) : undefined;

  return (
    /*
     * ⭐ **While the form shows its inline lookup error, it is a sign-in dead end** [the Operator, 2026-10-14,
     * «9. A»; 0461]: a *Need help?* press here reads *stuck at sign-in*. The lookup error is `rejectedFactors` —
     * the api refused the lookup. A Continue this screen blocked itself is not one: no lookup was spent.
     */
    <KioskScreen signInDeadEnd={rejectedFactors.length > 0}>
      <form
        className="flex w-full justify-center"
        noValidate
        onSubmit={(event) => {
          // Owned here rather than left to the browser: a native submission would navigate a kiosk away
          // from the app, and Enter in any of these fields triggers one.
          event.preventDefault();
          if (isPending) return;
          /*
           * The four-factor guard [decision 0032], where it belongs: an incomplete or malformed form is not
           * sent. The api would refuse it anyway — it validates before its throttle counts anything, so a
           * refusal never costs an attempt — and what checking here buys is the round trip and the answer:
           * each field says what it needs and the cursor lands in the first one.
           */
          if (invalid.length > 0) {
            setRefusals((count) => count + 1);
            focusFirstInvalidFactor(event.currentTarget, factors, invalid);
            return;
          }
          void submitLookup();
        }}
      >
        <KioskCard
          /*
            528 at desktop, and 448 everywhere else — the frame's own number, not a scale of the tablet's.
            `02 · Check-in — Desktop` [12055:10267] pads its content band 120px a side inside the 768 card
            (768 − 240 = 528) where `— iPad` [12055:10291] keeps 448 inside 553. Check-in is the only screen
            in the file that widens, so it says so here rather than moving the card's default.
          */
          contentWidth={528}
          footer={
            /*
              Full width at 375, then right-aligned and hugging its label from `sm` up. That is the frame's
              own switch, not a preference: `Actions` is VERTICAL with the button STRETCHed to 343 on
              `— Mobile` [12055:10315], and HORIZONTAL with MAX alignment around a 122-wide HUG button on
              both `— iPad` and `— Desktop`. It belongs to this screen rather than to `KioskCard`, because
              the frames disagree per screen — `07 · No jobs` still draws its action full width at 1440.
            */
            <div className="flex w-full flex-col sm:flex-row sm:justify-end">
              <Button disabled={isPending} type="submit">
                {t(isPending ? 'lookup.pending' : 'lookup.submit')}
              </Button>
            </div>
          }
          header={<BackLink onClick={askToExit} />}
        >
          <div className="flex w-full flex-col items-start gap-6 pb-8">
            <div className="flex w-full flex-col items-start gap-2">
              {/*
                SF Pro Display Bold 24/28 with -0.36 tracking, and plain black rather than `--foreground` —
                the frame uses true black for this heading only, which is why it is not tokenised here.
              */}
              <h1
                className="kiosk-heading-focus w-full text-2xl font-bold leading-7 tracking-[-0.36px] text-black"
                ref={heading}
                tabIndex={-1}
              >
                {t(headingKey)}
              </h1>
              <p className="w-full text-sm leading-6 text-[var(--muted-foreground)]">
                {t('checkIn.subheading')}
              </p>
            </div>

            <Field
              error={errorFor(CHECK_IN_FIELD.email, 'checkIn.emailInvalid')}
              id="check-in-email"
              inputMode="email"
              label={t('checkIn.email')}
              required
              onChange={(value) => {
                setFactor('email', value);
              }}
              placeholder={t('checkIn.emailPlaceholder')}
              type="email"
              value={factors.email}
            />

            <Field
              error={errorFor(CHECK_IN_FIELD.lastName, 'checkIn.lastNameRequired')}
              id="check-in-lastName"
              label={t('checkIn.lastName')}
              required
              maxLength={50}
              onChange={(value) => {
                setFactor('lastName', value);
              }}
              value={factors.lastName}
            />

            <DateOfBirthField
              day={factors.birthDay}
              /*
                One message for the PAIR, and only after a refused Continue — the frame draws a single
                validation slot under the two dropdowns, and the prototype's own rule for this group is
                "either half missing" rather than "exactly one chosen".
              */
              error={errorFor(CHECK_IN_FIELD.birthday, 'checkIn.dobBothRequired')}
              required
              month={factors.birthMonth}
              onDayChange={(value) => {
                setFactor('birthDay', value);
              }}
              onMonthChange={(value) => {
                setFactor('birthMonth', value);
              }}
            />

            <Field
              error={errorFor(CHECK_IN_FIELD.ssnLast4, 'checkIn.ssnLast4Invalid')}
              id="check-in-ssnLast4"
              inputMode="numeric"
              label={t('checkIn.ssnLast4')}
              required
              onChange={(value) => {
                /*
                  Digits only, then four at most: `ab12` is not a last-4, and a letter typed here could only
                  earn a refusal. The cap is applied AFTER the letters go, rather than as `maxLength`,
                  which the browser applies first — pasting `ab1234` would keep `ab12` and lose two digits.
                */
                setFactor('ssnLast4', digitsOnly(value).slice(0, SSN_LAST4_LENGTH));
              }}
              value={factors.ssnLast4}
            />

            <Callout>{t('checkIn.allFourRequired')}</Callout>
          </div>
        </KioskCard>
      </form>
    </KioskScreen>
  );
}

/**
 * The five controls **in the order they render**, so a refused Continue lands on the FIRST gap.
 *
 * Ids rather than refs, resolved through the form's own `elements` collection: the ids already exist —
 * every control needs one for its `<label for>` — and five refs threaded through two components would be
 * five more things to keep in step with the markup. Scoped to the form rather than the document, so it
 * cannot reach a control on some other screen.
 *
 * ⚠️ **This list and the markup move together.** "The first gap" means the first one the candidate can see,
 * so month precedes day here because `DateOfBirthField` draws month first — reordering one without the
 * other breaks nothing loudly, it just puts the cursor in the second box of a group whose first box is
 * empty, on a device with no keyboard and nobody standing beside it. Pinned by the DOM-order test.
 */
const FACTOR_CONTROL_IDS: readonly (readonly [CheckInFactorName, string])[] = [
  ['email', 'check-in-email'],
  ['lastName', 'check-in-lastName'],
  ['birthMonth', 'check-in-birthMonth'],
  ['birthDay', 'check-in-birthDay'],
  ['ssnLast4', 'check-in-ssnLast4'],
];

/**
 * Puts the cursor in the first factor that is missing or malformed.
 *
 * "Nothing happened" is indistinguishable from "the button is broken" on a device with no keyboard and
 * nobody standing beside it [the same reasoning as `useScrollToFirstError`]. Inside the date pair it is the
 * first EMPTY half, since the pair is only ever wrong for a half left unchosen.
 */
function focusFirstInvalidFactor(
  form: HTMLFormElement,
  factors: CheckInFactors,
  invalid: readonly CheckInField[],
): void {
  const target = FACTOR_CONTROL_IDS.find(([name]) => {
    const field = fieldOf(name);
    if (!invalid.includes(field)) return false;
    return field !== CHECK_IN_FIELD.birthday || factors[name] === '';
  });
  if (target === undefined) return;

  const control = form.elements.namedItem(target[1]);
  if (control instanceof HTMLElement) control.focus();
}
